import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import { HomeworkApi } from '../../services/homeworkApi';
import VideoPlayer from '../../components/learning/VideoPlayer';
import PostViewer from '../../components/learning/PostViewer';
import TestViewer from '../../components/learning/TestViewer';
import DocumentViewer, {
  type WhiteboardEditRequest,
} from '../../components/admin/DocumentViewer';
import CommentSection from '../../components/CommentSection';
import CourseReviewModal from '../../components/learning/CourseReviewModal';
import MaterialWhiteboardList from '../../components/learning/MaterialWhiteboardList';
import CourseWhiteboardGallery from '../../components/learning/CourseWhiteboardGallery';
import SampleProjectViewer from '../../components/materials/SampleProjectViewer';
import { DocumentIcon, ChevronRightIcon, ChevronLeftIcon, VideoCameraIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, Bars3Icon, FolderIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import CourseSkillLinksPanel from '../../components/skills/CourseSkillLinksPanel';
import SkillFileViewer from '../../components/skills/SkillFileViewer';
import type { SkillTreeNode } from '../../services/skillApi';
import { SampleProjectApi } from '../../services/sampleProjectApi';
import { useSecurePlaybackStream } from '../../hooks/useSecurePlaybackStream';
import SecureVideo from '../../components/learning/SecureVideo';
import VideoWatermark from '../../components/learning/VideoWatermark';
import { useAuthStore } from '../../store/authStore';
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay';
import { reportLearningActivity } from '../../services/learningActivityApi';

const SIDEBAR_PREF_KEY = 'student-learning-sidebar-collapsed';

type MaterialGroupKey = 'Slide' | 'Tài liệu tham khảo' | 'Khác' | 'Dự án mẫu';

const MATERIAL_GROUP_API: Record<MaterialGroupKey, 'slide' | 'reference' | 'other' | 'sample_project'> = {
  Slide: 'slide',
  'Tài liệu tham khảo': 'reference',
  Khác: 'other',
  'Dự án mẫu': 'sample_project',
};

function classifyMaterialGroup(type: string | null | undefined): MaterialGroupKey {
  const t = String(type || '').toLowerCase();
  if (t === 'sample_project' || t.includes('sample_project')) return 'Dự án mẫu';
  if (t === 'slide' || t.includes('slide')) return 'Slide';
  if (
    t === 'reference' ||
    t.includes('reference') ||
    t.includes('tham khảo') ||
    t.includes('tham khao')
  ) {
    return 'Tài liệu tham khảo';
  }
  return 'Khác';
}

function isSampleProjectMaterial(item: { type?: string } | null | undefined): boolean {
  return String(item?.type || '').toUpperCase() === 'SAMPLE_PROJECT';
}

function materialDownloadFilename(item: { title?: string; fileType?: string; fileUrl?: string }): string {
  const extFromType = (item.fileType || '').replace(/^\./, '').toLowerCase();
  const extFromUrl = (item.fileUrl || '').split('?')[0].split('.').pop()?.toLowerCase() || '';
  const ext = extFromType || (extFromUrl.length <= 5 ? extFromUrl : '');
  const base = (item.title || 'tai-lieu').trim() || 'tai-lieu';
  if (ext && !base.toLowerCase().endsWith(`.${ext}`)) return `${base}.${ext}`;
  return base;
}

async function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface CourseContentItem {
  id: number;
  courseId: number;
  contentType: 'FOLDER' | 'FORDER' | 'VIDEO' | 'POST' | 'TEST';
  name: string;
  detail?: string;
  objectId?: number;
  parentId?: number;
  position: number;
}

interface TestScoreSummary {
  percent: number;
  point: number;
  maxPoint: number;
  resultId?: number;
  passed?: boolean | null;
}

interface LiveMaterial {
  id: number;
  title: string;
  fileUrl: string;
  fileType?: string;
  type?: string;
}

interface LiveSessionItem {
  id: number;
  title: string;
  startTime: string;
  endTime: string;
  recordingUrl?: string | null;
  status: string;
  displayStatus?: string;
  sessionId?: number | null;
  sessionTitle?: string | null;
  sessionOrderIndex?: number | null;
  sessionContent?: string | null;
  sessionOutput?: string | null;
  sessionHomework?: string | null;
  materials?: LiveMaterial[];
  hasVideo?: boolean;
}

const StudentLearningPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const courseId = Number(id);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canTeach = Boolean(
    user?.roles?.some((r) => {
      const role = String(r).toUpperCase();
      return role === 'ADMIN' || role === 'SUPER_ADMIN';
    })
  );
  const [whiteboardRefresh, setWhiteboardRefresh] = useState(0);
  const [whiteboardEditRequest, setWhiteboardEditRequest] =
    useState<WhiteboardEditRequest | null>(null);
  
  const [courseData, setCourseData] = useState<any>(null);
  const [courseContent, setCourseContent] = useState<CourseContentItem[]>([]);
  const [completedIds, setCompletedIds] = useState<number[]>([]);
  const [testScoresByContentId, setTestScoresByContentId] = useState<Record<number, TestScoreSummary>>({});
  const [loading, setLoading] = useState(false);
  
  const [activeContentId, setActiveContentId] = useState<number | null>(null);
  const [activeLiveSessionId, setActiveLiveSessionId] = useState<number | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<'overview' | 'session-materials' | 'whiteboards' | 'comments' | 'homeworks'>('overview');
  const [homeworks, setHomeworks] = useState<any[]>([]);
  const [homeworksLoading, setHomeworksLoading] = useState(false);
  const [openQuizHistory, setOpenQuizHistory] = useState(false);
  const [sessionMaterialPreview, setSessionMaterialPreview] = useState<LiveMaterial | null>(null);

  // Materials & Classroom Live State
  const [rightSidebarTab, setRightSidebarTab] = useState<'curriculum' | 'materials' | 'live'>('curriculum');
  const [materials, setMaterials] = useState<any[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [activeMaterial, setActiveMaterial] = useState<any | null>(null);
  const [activeSkillFile, setActiveSkillFile] = useState<SkillTreeNode | null>(null);
  const [downloadingMaterialId, setDownloadingMaterialId] = useState<number | null>(null);
  const [downloadingGroup, setDownloadingGroup] = useState<MaterialGroupKey | null>(null);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialGroupTab, setMaterialGroupTab] = useState<MaterialGroupKey>('Slide');

  const MATERIAL_GROUP_TABS: MaterialGroupKey[] = ['Slide', 'Tài liệu tham khảo', 'Khác', 'Dự án mẫu'];
  
  // Classroom sessions (replaces old liveSessions)
  const [classroomData, setClassroomData] = useState<{
    myClassroom: any | null;
    viewFromClassroom: any | null;
  } | null>(null);
  const [loadingLive, setLoadingLive] = useState(false);
  // Collapse state for "my classroom" section
  const [myClassCollapsed, setMyClassCollapsed] = useState(false);
  const [videoSpeed, setVideoSpeed] = useState(1);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_PREF_KEY) !== '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_PREF_KEY, sidebarExpanded ? '0' : '1');
  }, [sidebarExpanded]);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;
    let cancelled = false;
    (async () => {
      try {
        setHomeworksLoading(true);
        const data = await HomeworkApi.studentList(Number(courseId));
        if (!cancelled) setHomeworks(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setHomeworks([]);
      } finally {
        if (!cancelled) setHomeworksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileSidebarOpen]);

  const fetchCourseData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/student/courses/${courseId}`);
      if (response.data.success) {
        setCourseData(response.data.data.courseInfo);
        const contents = response.data.data.courseContent || [];
        setCourseContent(contents);
        setCompletedIds(response.data.data.completedContentIds || []);
        setTestScoresByContentId(response.data.data.testScoresByContentId || {});

        // Find first playable item
        const firstPlayable = contents.find((c: any) => c.contentType !== 'FOLDER' && c.contentType !== 'FORDER');
        if (firstPlayable) {
          setActiveContentId(firstPlayable.id);
        }

        // Expand all folders (including nested) by default
        const folders = contents.filter((c: any) => c.contentType === 'FOLDER' || c.contentType === 'FORDER');
        const folderState: any = {};
        folders.forEach((f: any) => folderState[f.id] = true);
        setExpandedFolders(folderState);
      }
    } catch (error) {
      console.error('Failed to fetch course details', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rightSidebarTab === 'materials' && materials.length === 0 && !loadingMaterials) {
      fetchMaterials();
    }
    if (rightSidebarTab === 'live' && !classroomData && !loadingLive) {
      fetchClassroomSessions();
    }
  }, [rightSidebarTab]);

  const isVideoCourse = courseData?.method === 'VIDEO';

  useEffect(() => {
    if (isVideoCourse && rightSidebarTab === 'live') {
      setRightSidebarTab('curriculum');
    }
  }, [isVideoCourse, rightSidebarTab]);

  const fetchMaterials = async () => {
    setLoadingMaterials(true);
    try {
      const response = await apiClient.get(`/student/courses/${courseId}/materials`);
      if (response.data.success) {
        setMaterials(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch materials', error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const fetchClassroomSessions = async () => {
    setLoadingLive(true);
    try {
      const response = await apiClient.get(`/student/courses/${courseId}/classroom-sessions`);
      if (response.data.success) {
        const { myClassroom, viewFromClassroom } = response.data.data;
        setClassroomData({ myClassroom, viewFromClassroom });
        // Auto-collapse if course hasn't started yet
        if (myClassroom && !myClassroom.courseStarted) {
          setMyClassCollapsed(true);
        }

        const firstSelectableSession =
          myClassroom?.sessions?.find((session: any) => session.recordingUrl)
          || myClassroom?.sessions?.[0]
          || viewFromClassroom?.sessions?.find((session: any) => session.recordingUrl)
          || viewFromClassroom?.sessions?.[0]
          || null;

        if (firstSelectableSession) {
          setActiveLiveSessionId(firstSelectableSession.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch classroom sessions', error);
    } finally {
      setLoadingLive(false);
    }
  };

  const handleComplete = async (contentId: number, score?: TestScoreSummary) => {
    if (score) {
      setTestScoresByContentId((prev) => {
        const existing = prev[contentId];
        if (existing && score.percent < existing.percent) {
          return prev;
        }
        return {
          ...prev,
          [contentId]: {
            percent: score.percent,
            point: score.point,
            maxPoint: score.maxPoint,
            resultId: score.resultId ?? existing?.resultId,
            passed: score.passed ?? existing?.passed ?? null,
          },
        };
      });
      // Submit quiz already upserts progress server-side — reflect immediately in sidebar
      setCompletedIds((prev) => (prev.includes(contentId) ? prev : [...prev, contentId]));
      return;
    }

    if (completedIds.includes(contentId)) return;

    try {
      await apiClient.post(`/student/courses/${courseId}/progress`, { courseContentId: contentId });
      setCompletedIds((prev) => [...prev, contentId]);
    } catch (error) {
      console.error('Failed to update progress', error);
    }
  };

  const renderTestSubtitle = (item: CourseContentItem) => {
    const score = testScoresByContentId[item.id];
    if (score) {
      return (
        <span className="text-[11px] font-medium text-m2-secondary mt-0.5 flex items-center">
          <i className="fas fa-check-circle text-[8px] mr-1"></i>
          Đã đạt {score.percent}%
        </span>
      );
    }
    return (
      <span className="text-[11px] text-m2-on-surface-variant mt-0.5 flex items-center">
        <i className="fas fa-question text-[8px] mr-1 text-amber-400"></i> Bài kiểm tra (Quiz)
      </span>
    );
  };

  const closeMobileSidebar = () => setMobileSidebarOpen(false);

  const selectLesson = (contentId: number, options?: { openQuizHistory?: boolean }) => {
    setActiveContentId(contentId);
    setActiveMaterial(null);
    setActiveSkillFile(null);
    setActiveLiveSessionId(null);
    setSessionMaterialPreview(null);
    setActiveTab((tab) => (tab === 'session-materials' ? 'overview' : tab));
    setOpenQuizHistory(Boolean(options?.openQuizHistory));
    closeMobileSidebar();
    if (courseId > 0 && contentId > 0) {
      reportLearningActivity({
        eventType: 'CONTENT_OPEN',
        courseId,
        contentId,
      });
    }
  };

  const selectMaterial = (item: any) => {
    setActiveMaterial(item);
    setActiveSkillFile(null);
    setActiveContentId(null);
    setActiveLiveSessionId(null);
    setSessionMaterialPreview(null);
    setActiveTab((tab) => (tab === 'session-materials' || tab === 'whiteboards' ? 'overview' : tab));
    closeMobileSidebar();
  };

  const openMaterialFromGallery = async (materialId: number) => {
    let list = materials;
    if (list.length === 0) {
      try {
        const response = await apiClient.get(`/student/courses/${courseId}/materials`);
        if (response.data.success) {
          list = response.data.data || [];
          setMaterials(list);
        }
      } catch (error) {
        console.error('Failed to fetch materials', error);
      }
    }
    const item = list.find((m: any) => Number(m.id) === Number(materialId));
    if (!item) {
      toast.error('Không tìm thấy tài liệu gắn với bảng này');
      return;
    }
    setRightSidebarTab('materials');
    setMaterialGroupTab(classifyMaterialGroup(item.type));
    selectMaterial(item);
  };

  const selectSkillFile = (node: SkillTreeNode) => {
    setActiveSkillFile(node);
    setActiveMaterial(null);
    setActiveContentId(null);
    setActiveLiveSessionId(null);
    setSessionMaterialPreview(null);
    setActiveTab((tab) => (tab === 'session-materials' ? 'overview' : tab));
    closeMobileSidebar();
  };

  const selectLiveSession = (sessionId: number) => {
    setActiveLiveSessionId(sessionId);
    setActiveContentId(null);
    setActiveMaterial(null);
    setActiveSkillFile(null);
    setSessionMaterialPreview(null);
    setActiveTab('overview');
    closeMobileSidebar();
  };

  const groupMaterialsByType = () => {
    const groups: Record<MaterialGroupKey, any[]> = {
      Slide: [],
      'Tài liệu tham khảo': [],
      Khác: [],
      'Dự án mẫu': [],
    };

    materials.forEach((mat) => {
      groups[classifyMaterialGroup(mat.type)].push(mat);
    });

    return groups;
  };

  const filterMaterialsInTab = (items: any[]) => {
    const q = materialSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((mat) => {
      const hay = `${mat.title || ''} ${mat.fileType || ''} ${mat.type || ''}`.toLowerCase();
      return hay.includes(q);
    });
  };

  const downloadMaterialFile = async (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isSampleProjectMaterial(item)) {
      try {
        setDownloadingMaterialId(item.id);
        await SampleProjectApi.downloadProject(
          courseId,
          item.id,
          item.title || `du-an-mau-${item.id}`
        );
        toast.success(`Đã tải ${item.title || 'dự án mẫu'}`);
      } catch (err: any) {
        console.error(err);
        toast.error(err?.message || 'Không tải được dự án mẫu');
      } finally {
        setDownloadingMaterialId(null);
      }
      return;
    }
    if (!item?.fileUrl) {
      toast.error('Không có file để tải');
      return;
    }
    try {
      setDownloadingMaterialId(item.id);
      const res = await fetch(item.fileUrl);
      if (!res.ok) throw new Error('Tải thất bại');
      const blob = await res.blob();
      await triggerBlobDownload(blob, materialDownloadFilename(item));
      toast.success(`Đã tải ${item.title || 'file'}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Không tải được file');
    } finally {
      setDownloadingMaterialId(null);
    }
  };

  const downloadMaterialGroup = async (groupName: MaterialGroupKey) => {
    if (groupName === 'Dự án mẫu') {
      const items = groupMaterialsByType()['Dự án mẫu'] || [];
      if (items.length === 0) {
        toast.error('Chưa có dự án mẫu');
        return;
      }
      try {
        setDownloadingGroup(groupName);
        for (const item of items) {
          await SampleProjectApi.downloadProject(
            courseId,
            item.id,
            item.title || `du-an-mau-${item.id}`
          );
        }
        toast.success(`Đã tải ${items.length} dự án mẫu`);
      } catch (err: any) {
        console.error(err);
        toast.error(err?.message || 'Không tải được dự án mẫu');
      } finally {
        setDownloadingGroup(null);
      }
      return;
    }
    try {
      setDownloadingGroup(groupName);
      const group = MATERIAL_GROUP_API[groupName];
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `/api/v1/student/courses/${courseId}/materials/download-zip?group=${group}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Tải ZIP thất bại');
      }
      const blob = await res.blob();
      const slug =
        group === 'slide' ? 'slide' : group === 'reference' ? 'tai-lieu-tham-khao' : 'khac';
      await triggerBlobDownload(blob, `khoa-hoc-${courseId}-${slug}.zip`);
      toast.success(`Đã tải nhóm ${groupName}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Không tải được thư mục');
    } finally {
      setDownloadingGroup(null);
    }
  };

  const toggleFolder = (folderId: number) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const activeContent = courseContent.find(c => c.id === activeContentId);

  // Lấy danh sách bài học có thể xem theo đúng thứ tự hiển thị trên sidebar
  // (DFS traversal theo cây folder, giống cách renderCurriculumItems hoạt động)
  const getPlayableInOrder = (parentId?: number): CourseContentItem[] => {
    const children = courseContent
      .filter(c => c.parentId === parentId)
      .sort((a, b) => a.position - b.position);
    const result: CourseContentItem[] = [];
    for (const child of children) {
      if (child.contentType === 'FOLDER' || child.contentType === 'FORDER') {
        result.push(...getPlayableInOrder(child.id));
      } else {
        result.push(child);
      }
    }
    return result;
  };
  const playableContents = getPlayableInOrder(undefined);
  const activeContentIndex = playableContents.findIndex(item => item.id === activeContentId);

  // Trả về danh sách tất cả folder tổ tiên (từ root đến cha trực tiếp) của một item
  const getAncestorFolderIds = (itemParentId: number | undefined): number[] => {
    if (!itemParentId) return [];
    const parent = courseContent.find(c => c.id === itemParentId);
    if (!parent) return [];
    return [...getAncestorFolderIds(parent.parentId), parent.id];
  };

  const goToContent = (direction: -1 | 1) => {
    const nextItem = playableContents[activeContentIndex + direction];
    if (!nextItem) return;

    // Tự động mở tất cả folder chứa bài tiếp theo (kể cả folder lồng nhau)
    const ancestorIds = getAncestorFolderIds(nextItem.parentId);
    if (ancestorIds.length > 0) {
      setExpandedFolders(prev => {
        const updated = { ...prev };
        ancestorIds.forEach(id => { updated[id] = true; });
        return updated;
      });
    }

    setActiveContentId(nextItem.id);
    setActiveMaterial(null);
    setActiveSkillFile(null);
    setActiveLiveSessionId(null);
    setSessionMaterialPreview(null);
    setActiveTab((tab) => (tab === 'session-materials' ? 'overview' : tab));
    setOpenQuizHistory(false);
  };

  const materialNavList = activeMaterial
    ? materials.filter(
        (m) => classifyMaterialGroup(m.type) === classifyMaterialGroup(activeMaterial.type)
      )
    : [];
  const activeMaterialIndex = materialNavList.findIndex((m) => m.id === activeMaterial?.id);
  const goToMaterial = (direction: -1 | 1) => {
    const next = materialNavList[activeMaterialIndex + direction];
    if (!next) return;
    setMaterialGroupTab(classifyMaterialGroup(next.type));
    selectMaterial(next);
  };

  const activeLiveSession = classroomData?.myClassroom?.sessions?.find((session: any) => session.id === activeLiveSessionId)
    || classroomData?.viewFromClassroom?.sessions?.find((session: any) => session.id === activeLiveSessionId)
    || null;

  const liveSessionNavList: any[] = (() => {
    if (!activeLiveSessionId || !classroomData) return [];
    if (classroomData.myClassroom?.sessions?.some((s: any) => s.id === activeLiveSessionId)) {
      return classroomData.myClassroom.sessions;
    }
    if (classroomData.viewFromClassroom?.sessions?.some((s: any) => s.id === activeLiveSessionId)) {
      return classroomData.viewFromClassroom.sessions;
    }
    return [];
  })();
  const activeLiveSessionIndex = liveSessionNavList.findIndex((s: any) => s.id === activeLiveSessionId);
  const goToLiveSessionNav = (direction: -1 | 1) => {
    const next = liveSessionNavList[activeLiveSessionIndex + direction];
    if (!next) return;
    selectLiveSession(next.id);
  };

  const sessionMaterials: LiveMaterial[] = activeLiveSession?.materials || [];
  const sessionMaterialsCount = sessionMaterials.length;
  const activeSessionMaterialIndex = sessionMaterialPreview
    ? sessionMaterials.findIndex((m) => m.id === sessionMaterialPreview.id)
    : -1;
  const goToSessionMaterial = (direction: -1 | 1) => {
    if (activeSessionMaterialIndex < 0) return;
    const next = sessionMaterials[activeSessionMaterialIndex + direction];
    if (next) setSessionMaterialPreview(next);
  };

  const navChevronClass =
    'hidden lg:inline-flex absolute top-1/2 z-40 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-eh-forest-ink text-eh-cream shadow-md ring-1 ring-eh-sage/40 transition hover:bg-eh-forest-shadow disabled:cursor-not-allowed disabled:bg-eh-forest-ink/30 disabled:text-eh-cream/70 disabled:shadow-none';
  const rootFolders = courseContent.filter(c => (c.contentType === 'FOLDER' || c.contentType === 'FORDER') && !c.parentId);
  const rootLooseItems = courseContent.filter(c => !c.parentId && c.contentType !== 'FOLDER' && c.contentType !== 'FORDER');
  
  // Calculate Progress
  const totalPlayable = courseContent.filter(c => c.contentType !== 'FOLDER' && c.contentType !== 'FORDER').length;
  const progressPercent = totalPlayable === 0 ? 0 : Math.round((completedIds.length / totalPlayable) * 100);
  const isQuizContent = activeContent?.contentType === 'TEST';
  const isReadingContent =
    activeContent?.contentType === 'POST' ||
    isQuizContent ||
    Boolean(activeSkillFile) ||
    Boolean(activeMaterial);

  const getIconForType = (type: string, isCompleted: boolean) => {
    if (isCompleted) return "fas fa-check-circle text-m2-secondary";
    switch (type) {
      case 'VIDEO': return "fas fa-play-circle text-red-500";
      case 'POST': return "fas fa-file-alt text-sky-400";
      case 'TEST': return "fas fa-question-circle text-amber-400";
      case 'FOLDER':
      case 'FORDER': return "fas fa-folder text-amber-500";
      default: return "fas fa-circle text-m2-outline-variant";
    }
  };

  const isFolderType = (type: string) => type === 'FOLDER' || type === 'FORDER';

  const getDirectChildren = (parentId: number) =>
    courseContent
      .filter(c => c.parentId === parentId)
      .sort((a, b) => a.position - b.position);

  const countPlayableDescendants = (folderId: number): { total: number; completed: number } => {
    const children = getDirectChildren(folderId);
    return children.reduce(
      (acc, child) => {
        if (isFolderType(child.contentType)) {
          const nested = countPlayableDescendants(child.id);
          return { total: acc.total + nested.total, completed: acc.completed + nested.completed };
        }
        return {
          total: acc.total + 1,
          completed: acc.completed + (completedIds.includes(child.id) ? 1 : 0),
        };
      },
      { total: 0, completed: 0 }
    );
  };

  const renderCurriculumItems = (parentId: number, depth: number = 0, indexPrefix: string = '') => {
    const children = getDirectChildren(parentId);
    return children.map((item, itemIdx) => {
      const itemNumber = indexPrefix ? `${indexPrefix}.${itemIdx + 1}` : `${itemIdx + 1}`;
      const paddingLeft = 20 + depth * 16;

      if (isFolderType(item.contentType)) {
        const nestedStats = countPlayableDescendants(item.id);
        const isExpanded = expandedFolders[item.id] !== false;
        return (
          <div key={item.id}>
            <button
              type="button"
              onClick={() => toggleFolder(item.id)}
              className="w-full text-left py-2.5 flex items-start space-x-3 transition-colors hover:bg-m2-surface-high border-l-4 border-transparent"
              style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '20px' }}
            >
              <div className="pt-0.5">
                <i className={`${getIconForType(item.contentType, false)} text-[14px]`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-m2-on-surface leading-tight">
                  {itemNumber}. {item.name}
                </span>
                <span className="text-[11px] text-m2-on-surface-variant mt-0.5 flex items-center gap-2">
                  <span>{nestedStats.completed} / {nestedStats.total} bài</span>
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-[9px]`}></i>
                </span>
              </div>
            </button>
            {isExpanded && renderCurriculumItems(item.id, depth + 1, itemNumber)}
          </div>
        );
      }

      return (
        <div
          key={item.id}
          className={`w-full flex items-start transition-colors border-l-4 ${
            activeContentId === item.id
              ? 'bg-m2-primary/10 border-m2-primary'
              : 'hover:bg-m2-surface-high border-transparent'
          }`}
          style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '12px' }}
        >
          <button
            type="button"
            onClick={() => selectLesson(item.id)}
            className="flex-1 text-left py-3 flex items-start space-x-3 min-w-0"
          >
            <div className="pt-0.5 relative">
              <i className={`${getIconForType(item.contentType, completedIds.includes(item.id))} text-[16px]`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <span className={`block text-sm leading-tight ${
                activeContentId === item.id
                  ? 'font-bold text-m2-primary'
                  : completedIds.includes(item.id)
                    ? 'font-medium text-m2-secondary'
                    : 'text-m2-on-surface'
              }`}>
                {itemNumber}. {item.name}
              </span>
              {item.contentType === 'VIDEO' && <span className="text-[11px] text-m2-on-surface-variant mt-0.5 flex items-center"><i className="fas fa-play text-[8px] mr-1 text-red-500"></i> Video</span>}
              {item.contentType === 'POST' && <span className="text-[11px] text-m2-on-surface-variant mt-0.5 flex items-center"><i className="fas fa-file-alt text-[8px] mr-1 text-sky-400"></i> Tài liệu đọc</span>}
              {item.contentType === 'TEST' && renderTestSubtitle(item)}
            </div>
          </button>
          {item.contentType === 'TEST' && item.objectId ? (
            <button
              type="button"
              title="Xem lịch sử làm bài"
              onClick={(e) => {
                e.stopPropagation();
                selectLesson(item.id, { openQuizHistory: true });
              }}
              className="mt-3 mr-1 shrink-0 w-8 h-8 rounded-lg text-m2-primary hover:bg-m2-primary/15 flex items-center justify-center"
            >
              <i className="fas fa-history text-sm"></i>
            </button>
          ) : null}
        </div>
      );
    });
  };

  const renderSidebarInner = (opts?: { showCollapse?: boolean }) => (
    <div className="student-learning-sidebar w-full h-full flex flex-col overflow-hidden bg-white">
      <div className="bg-white border-b border-m2-outline sticky top-0 z-10 flex text-xs shrink-0 items-stretch">
        <button
          className={`flex-1 py-3 px-1 font-bold text-center border-b-2 transition-colors ${rightSidebarTab === 'curriculum' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface'}`}
          onClick={() => setRightSidebarTab('curriculum')}
        >
          Nội dung
        </button>
        <button
          className={`flex-1 py-3 px-1 font-bold text-center border-b-2 transition-colors ${rightSidebarTab === 'materials' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface'}`}
          onClick={() => setRightSidebarTab('materials')}
        >
          Tài liệu
        </button>
        {!isVideoCourse && (
          <button
            className={`flex-1 py-3 px-1 font-bold text-center border-b-2 transition-colors ${rightSidebarTab === 'live' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface'}`}
            onClick={() => setRightSidebarTab('live')}
          >
            <span className="inline-flex items-center justify-center gap-1">
              Lớp học
              {activeLiveSessionId !== null && (
                <span className="h-1.5 w-1.5 rounded-full bg-m2-primary animate-pulse" title="Đang xem video buổi học"></span>
              )}
            </span>
          </button>
        )}
        {opts?.showCollapse && (
          <button
            type="button"
            onClick={() => setSidebarExpanded(false)}
            className="hidden lg:flex shrink-0 w-9 items-center justify-center border-b-2 border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:bg-m2-surface-high transition-colors"
            title="Ẩn menu để xem rộng hơn"
            aria-label="Ẩn menu"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-grow overflow-y-auto custom-scrollbar">
        {rightSidebarTab === 'curriculum' && (
          <div>
            {rootLooseItems.length > 0 && (
              <div className="border-b border-m2-outline bg-white py-1">
                {rootLooseItems.map((item, itemIdx) => (
                  <div
                    key={item.id}
                    className={`w-full flex items-start transition-colors border-l-4 ${
                      activeContentId === item.id
                        ? 'bg-m2-primary/10 border-m2-primary'
                        : 'hover:bg-m2-surface-high border-transparent'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => selectLesson(item.id)}
                      className="flex-1 text-left px-5 py-3 flex items-start space-x-3 min-w-0"
                    >
                      <div className="pt-0.5">
                        <i className={`${getIconForType(item.contentType, completedIds.includes(item.id))} text-[16px]`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`block text-sm leading-tight ${
                          activeContentId === item.id
                            ? 'font-bold text-m2-primary'
                            : completedIds.includes(item.id)
                              ? 'font-medium text-m2-secondary'
                              : 'text-m2-on-surface'
                        }`}>
                          {itemIdx + 1}. {item.name}
                        </span>
                        {item.contentType === 'VIDEO' && (
                          <span className="text-[11px] text-m2-on-surface-variant mt-0.5 flex items-center">
                            <i className="fas fa-play text-[8px] mr-1 text-red-500"></i> Video
                          </span>
                        )}
                        {item.contentType === 'POST' && (
                          <span className="text-[11px] text-m2-on-surface-variant mt-0.5 flex items-center">
                            <i className="fas fa-file-alt text-[8px] mr-1 text-sky-400"></i> Tài liệu đọc
                          </span>
                        )}
                        {item.contentType === 'TEST' && renderTestSubtitle(item)}
                      </div>
                    </button>
                    {item.contentType === 'TEST' && item.objectId ? (
                      <button
                        type="button"
                        title="Xem lịch sử làm bài"
                        onClick={() => selectLesson(item.id, { openQuizHistory: true })}
                        className="mt-3 mr-3 shrink-0 w-8 h-8 rounded-lg text-m2-primary hover:bg-m2-primary/15 flex items-center justify-center"
                      >
                        <i className="fas fa-history text-sm"></i>
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            {rootFolders.map((folder, index) => {
              const nestedStats = countPlayableDescendants(folder.id);
              return (
                <div key={folder.id} className="border-b border-m2-outline bg-white">
                  <button
                    type="button"
                    onClick={() => toggleFolder(folder.id)}
                    className="w-full text-left px-5 py-4 hover:bg-m2-surface-low transition flex items-center justify-between group"
                  >
                    <div>
                      <h4 className="font-bold text-m2-on-surface text-[15px] group-hover:text-m2-primary transition-colors">
                        Phần {index + 1}: {folder.name}
                      </h4>
                      <p className="text-xs text-m2-on-surface-variant mt-1">
                        {nestedStats.completed} / {nestedStats.total} bài học
                      </p>
                    </div>
                    <i className={`fas fa-chevron-${expandedFolders[folder.id] ? 'up' : 'down'} text-m2-outline text-sm`}></i>
                  </button>
                  {expandedFolders[folder.id] && (
                    <div className="bg-m2-surface-dim py-1">
                      {renderCurriculumItems(folder.id)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {rightSidebarTab === 'materials' && (
          <div className="p-4 space-y-6">
            {loadingMaterials ? (
              <div className="flex items-center justify-center py-10">
                <i className="fas fa-spinner fa-spin text-2xl text-m2-primary mr-2"></i>
              </div>
            ) : materials.length === 0 ? (
              <div className="text-m2-on-surface-variant text-center py-6 text-sm">Không có tài liệu nào.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-1 overflow-x-auto rounded-lg border border-m2-outline-variant bg-m2-surface-low p-1">
                  {MATERIAL_GROUP_TABS.map((tab) => {
                    const count = groupMaterialsByType()[tab]?.length || 0;
                    const active = materialGroupTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setMaterialGroupTab(tab)}
                        className={`shrink-0 flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                          active
                            ? 'bg-m2-primary text-m2-on-primary'
                            : 'text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-on-surface'
                        }`}
                      >
                        {tab === 'Tài liệu tham khảo' ? 'Tham khảo' : tab === 'Dự án mẫu' ? 'Dự án' : tab}
                        <span className="ml-0.5 opacity-80">({count})</span>
                      </button>
                    );
                  })}
                </div>

                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-m2-on-surface-variant" />
                  <input
                    type="search"
                    value={materialSearch}
                    onChange={(e) => setMaterialSearch(e.target.value)}
                    placeholder={`Tìm trong ${materialGroupTab}...`}
                    className="w-full rounded-md border border-m2-outline-variant bg-m2-surface-container py-2 pl-9 pr-3 text-sm text-m2-on-surface placeholder:text-m2-on-surface-variant focus:border-m2-primary focus:outline-none"
                  />
                </div>

                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-m2-on-surface-variant">
                    {materialGroupTab}
                  </h4>
                  <button
                    type="button"
                    disabled={downloadingGroup === materialGroupTab || (groupMaterialsByType()[materialGroupTab]?.length || 0) === 0}
                    onClick={() => downloadMaterialGroup(materialGroupTab)}
                    className="inline-flex items-center gap-1 rounded-md border border-m2-outline px-2 py-1 text-[11px] text-m2-on-surface hover:bg-m2-surface-high disabled:opacity-50"
                    title={`Tải tất cả ${materialGroupTab} (ZIP)`}
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    {downloadingGroup === materialGroupTab ? 'Đang tải...' : 'Tải cả nhóm'}
                  </button>
                </div>

                {(() => {
                  const items = filterMaterialsInTab(groupMaterialsByType()[materialGroupTab] || []);
                  if (items.length === 0) {
                    return (
                      <p className="py-4 text-center text-sm text-m2-on-surface-variant">
                        {materialSearch.trim()
                          ? 'Không tìm thấy tài liệu phù hợp.'
                          : `Chưa có tài liệu trong ${materialGroupTab}.`}
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {items.map((item: any) => (
                        <div
                          key={item.id}
                          className={`flex items-start gap-1 rounded-lg border transition-colors ${
                            activeMaterial?.id === item.id
                              ? 'border-m2-primary/30 bg-m2-primary/10'
                              : 'border-m2-outline-variant bg-m2-surface-container hover:border-m2-primary/40 hover:bg-m2-surface-low'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => selectMaterial(item)}
                            className="flex min-w-0 flex-1 items-start space-x-3 p-3 text-left"
                          >
                            <div
                              className={`${
                                activeMaterial?.id === item.id
                                  ? 'text-m2-primary'
                                  : 'text-m2-on-surface-variant'
                              } pt-0.5`}
                            >
                              {isSampleProjectMaterial(item) ? (
                                <FolderIcon className="w-5 h-5 text-amber-600" />
                              ) : (
                                <DocumentIcon className="w-5 h-5" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <h5
                                className={`line-clamp-2 text-sm font-medium ${
                                  activeMaterial?.id === item.id
                                    ? 'text-m2-primary'
                                    : 'text-m2-on-surface'
                                }`}
                              >
                                {item.title}
                              </h5>
                              <span className="mt-1 block text-[10px] uppercase text-m2-on-surface-variant">
                                {isSampleProjectMaterial(item) ? 'FOLDER' : item.fileType || 'FILE'}
                              </span>
                            </div>
                          </button>
                          <button
                            type="button"
                            title={isSampleProjectMaterial(item) ? 'Tải ZIP dự án' : 'Tải file này'}
                            disabled={downloadingMaterialId === item.id}
                            onClick={(e) => downloadMaterialFile(item, e)}
                            className="mr-2 mt-2 shrink-0 rounded p-1.5 text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary disabled:opacity-50"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {courseId > 0 && (
              <CourseSkillLinksPanel
                courseId={courseId}
                selectedFileId={activeSkillFile?.id}
                onSelectFile={selectSkillFile}
              />
            )}
          </div>
        )}

        {rightSidebarTab === 'live' && !isVideoCourse && (
          <div className="p-4 space-y-4">
            {loadingLive ? (
              <div className="flex items-center justify-center py-10">
                <i className="fas fa-spinner fa-spin text-2xl text-m2-primary mr-2"></i>
              </div>
            ) : !classroomData || !classroomData.myClassroom ? (
              <div className="text-m2-on-surface-variant text-center py-6 text-sm">Bạn chưa được xếp vào lớp học nào.</div>
            ) : (
              <>
                <div className="rounded-xl border border-m2-outline-variant overflow-hidden">
                  <button
                    onClick={() => setMyClassCollapsed(c => !c)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-m2-primary text-m2-on-primary hover:bg-eh-forest-shadow transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <i className="fas fa-chalkboard-teacher text-sm"></i>
                      <span className="font-bold text-sm">Lớp của tôi</span>
                      {classroomData.myClassroom.name && (
                        <span className="text-[11px] bg-m2-surface-container/20 px-2 py-0.5 rounded-full">
                          {classroomData.myClassroom.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {classroomData.myClassroom.courseFinished ? (
                        <span className="bg-m2-surface-container/30 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          Hoàn thành
                        </span>
                      ) : classroomData.myClassroom.courseStarted ? (
                        <span className="bg-emerald-400 text-emerald-950 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          Đang học
                        </span>
                      ) : (
                        <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          Chưa bắt đầu
                        </span>
                      )}
                      <i className={`fas fa-chevron-${myClassCollapsed ? 'down' : 'up'} text-xs`}></i>
                    </div>
                  </button>

                  {!myClassCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {classroomData.myClassroom.sessions.length === 0 ? (
                        <p className="text-xs text-m2-outline text-center py-4">Chưa có buổi học nào được lên lịch.</p>
                      ) : (
                        classroomData.myClassroom.sessions.map((session: any) => (
                          <ClassroomSessionCard
                            key={session.id}
                            session={session}
                            isActive={activeLiveSessionId === session.id}
                            onSelect={() => selectLiveSession(session.id)}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>

                {classroomData.viewFromClassroom && (
                  <div className="rounded-xl border border-eh-slate overflow-hidden">
                    <div className="px-4 py-3 bg-eh-slate text-eh-forest-ink">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-eye text-sm"></i>
                        <span className="font-bold text-sm">Lớp {classroomData.viewFromClassroom.name}</span>
                        <span className="text-[10px] bg-m2-surface-container/20 px-2 py-0.5 rounded-full">Xem thêm</span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {classroomData.viewFromClassroom.sessions.length === 0 ? (
                        <p className="text-xs text-m2-outline text-center py-4">Chưa có buổi học nào.</p>
                      ) : (
                        classroomData.viewFromClassroom.sessions.map((session: any) => (
                          <ClassroomSessionCard
                            key={session.id}
                            session={session}
                            isActive={activeLiveSessionId === session.id}
                            compact
                            onSelect={() => selectLiveSession(session.id)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex flex-col font-sans bg-m2-background text-m2-on-surface overflow-x-hidden">
      {/* Ease Health learning header - Sticky */}
      <header className="h-14 bg-eh-forest-ink text-eh-cream flex items-center justify-between gap-2 px-3 lg:px-6 shrink-0 z-30 shadow-md sticky top-0">
        <div className="flex items-center gap-2 lg:gap-4 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => navigate('/student/my-courses')}
            className="hover:text-eh-sage transition shrink-0 p-1"
            aria-label="Quay lại"
          >
             <i className="fas fa-arrow-left"></i>
          </button>
          <h1 className="text-sm lg:text-base font-bold truncate max-w-[40vw] sm:max-w-xs lg:max-w-xl">
            {courseData?.name || 'Đang tải...'}
          </h1>
        </div>
        <div className="flex items-center gap-2 lg:gap-4 text-sm shrink-0">
           <div className="flex items-center gap-2">
             <span className="font-bold text-eh-cream text-xs lg:text-sm tabular-nums">
               {completedIds.length}/{totalPlayable}
             </span>
             <span className="hidden lg:inline text-eh-mint">bài học</span>
             <div className="w-14 lg:w-24 bg-eh-cream/25 rounded-full h-2">
                <div className="bg-eh-sage h-2 rounded-full" style={{ width: `${progressPercent}%` }}></div>
             </div>
           </div>
           <button
             type="button"
             onClick={() => setReviewModalOpen(true)}
             className="bg-eh-cream/10 hover:bg-eh-cream/20 p-2 lg:px-4 lg:py-1.5 rounded transition text-xs font-semibold shadow-sm"
             title="Đánh giá khóa học"
             aria-label="Đánh giá khóa học"
           >
             <i className="fas fa-star lg:hidden"></i>
             <span className="hidden lg:inline">Đánh giá khóa học</span>
           </button>
           <button
             type="button"
             onClick={() => setMobileSidebarOpen(true)}
             className="lg:hidden bg-m2-primary text-m2-on-primary px-2.5 py-1.5 rounded text-xs font-bold shadow-sm"
           >
             Nội dung
           </button>
           <button
             type="button"
             onClick={() => setSidebarExpanded((v) => !v)}
             className="hidden lg:inline-flex items-center gap-1.5 bg-eh-cream/10 hover:bg-eh-cream/20 px-2.5 py-1.5 rounded text-xs font-bold shadow-sm transition"
             title={sidebarExpanded ? 'Ẩn menu để xem rộng hơn' : 'Hiện menu nội dung'}
             aria-label={sidebarExpanded ? 'Ẩn menu' : 'Hiện menu'}
             aria-pressed={sidebarExpanded}
           >
             {sidebarExpanded ? (
               <ChevronRightIcon className="h-4 w-4" />
             ) : (
               <Bars3Icon className="h-4 w-4" />
             )}
             <span>{sidebarExpanded ? 'Ẩn menu' : 'Nội dung'}</span>
           </button>
        </div>
      </header>
      
      {/* Main Area */}
      <main className="flex flex-col lg:flex-row flex-grow min-w-0">
        {/* Left/Main Content Area - scrolls with the page (browser native scrollbar) */}
        <div className="flex-grow flex flex-col bg-m2-surface-container relative transition-all duration-300 w-full min-w-0">
          
          {/* Player / Quiz Frame — aspect-video on mobile, theater on lg+ */}
          <div
            className={`w-full relative flex items-center justify-center shadow-inner ${
              isReadingContent
                ? 'bg-m2-surface-high p-2 lg:p-4 min-h-[60vh] h-[calc(100dvh-8rem)] lg:h-[calc(100vh-3.5rem)] lg:min-h-[360px]'
                : 'bg-[#111111] p-0 lg:p-4 h-auto aspect-video lg:aspect-auto lg:h-[calc(100vh-3.5rem)] lg:min-h-[360px]'
            }`}
          >
            {loading ? (
              <div className="flex justify-center flex-col items-center">
                <i className="fas fa-spinner fa-spin text-4xl text-m2-primary mb-4"></i>
                <p className={isReadingContent ? 'text-m2-on-surface-variant' : 'text-m2-outline'}>Đang tải dữ liệu khóa học...</p>
              </div>
            ) : activeLiveSession ? (
              <div className="w-full h-full flex flex-col relative min-h-0 overflow-hidden">
                <button
                  type="button"
                  onClick={() => goToLiveSessionNav(-1)}
                  disabled={activeLiveSessionIndex <= 0}
                  title="Buổi trước"
                  aria-label="Buổi trước"
                  className={`${navChevronClass} left-2 lg:left-3`}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => goToLiveSessionNav(1)}
                  disabled={activeLiveSessionIndex < 0 || activeLiveSessionIndex >= liveSessionNavList.length - 1}
                  title="Buổi sau"
                  aria-label="Buổi sau"
                  className={`${navChevronClass} right-2 lg:right-3`}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <LiveSessionVideoPlayer
                  session={activeLiveSession}
                  videoSpeed={videoSpeed}
                  onSpeedChange={setVideoSpeed}
                  onEnded={() => goToLiveSessionNav(1)}
                />
              </div>
            ) : (!activeContent && !activeMaterial && !activeSkillFile) ? (
              <div className="bg-eh-cream p-10 rounded-xl text-center border border-eh-border text-eh-charcoal shadow-eh-soft">
                <div className="w-20 h-20 bg-eh-keylime text-eh-forest-ink rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-book-reader text-3xl"></i>
                </div>
                <h2 className="text-2xl font-bold mb-2">Chào mừng bạn đến với khóa học</h2>
                <p className="text-m2-outline">Hãy chọn một bài học ở mục Nội dung để bắt đầu.</p>
              </div>
            ) : activeSkillFile ? (
              <div className="w-full h-full min-h-0 bg-m2-surface-container rounded-lg overflow-hidden shadow-sm">
                <SkillFileViewer node={activeSkillFile} courseId={courseId} />
              </div>
            ) : activeMaterial ? (
              <div className="w-full h-full min-h-0 flex flex-col bg-m2-surface-container rounded-lg shadow-sm relative">
                <button
                  type="button"
                  onClick={() => goToMaterial(-1)}
                  disabled={activeMaterialIndex <= 0}
                  title="Tài liệu trước"
                  aria-label="Tài liệu trước"
                  className={`${navChevronClass} left-2 lg:left-3`}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => goToMaterial(1)}
                  disabled={activeMaterialIndex < 0 || activeMaterialIndex >= materialNavList.length - 1}
                  title="Tài liệu sau"
                  aria-label="Tài liệu sau"
                  className={`${navChevronClass} right-2 lg:right-3`}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                {isSampleProjectMaterial(activeMaterial) ? (
                  <div className="flex-1 min-h-0 relative p-3">
                    <SampleProjectViewer
                      courseId={courseId}
                      materialId={activeMaterial.id}
                      title={activeMaterial.title}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-h-0 overflow-hidden relative">
                      <DocumentViewer
                        fileUrl={activeMaterial.fileUrl}
                        fileType={activeMaterial.fileType || ''}
                        title={activeMaterial.title}
                        materialId={activeMaterial.id}
                        courseId={courseId}
                        canAnnotate={canTeach}
                        compact
                        onWhiteboardSaved={() => setWhiteboardRefresh((k) => k + 1)}
                        editRequest={
                          whiteboardEditRequest?.materialId === activeMaterial.id
                            ? whiteboardEditRequest
                            : null
                        }
                        onEditRequestHandled={() => setWhiteboardEditRequest(null)}
                      />
                    </div>
                    <div className="shrink-0 border-t border-m2-outline-variant px-3 pb-2">
                      <MaterialWhiteboardList
                        courseId={courseId}
                        materialId={activeMaterial.id}
                        refreshKey={whiteboardRefresh}
                        onEditWhiteboard={canTeach ? setWhiteboardEditRequest : undefined}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : activeContent ? (
              <div className="w-full h-full flex flex-col relative min-h-0 overflow-hidden">
                {activeContent.contentType === 'VIDEO' && (
                  <>
                    <button
                      type="button"
                      onClick={() => goToContent(-1)}
                      disabled={activeContentIndex <= 0}
                      title="Bài trước"
                      aria-label="Bài trước"
                      className={`${navChevronClass} left-2 lg:left-3`}
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => goToContent(1)}
                      disabled={activeContentIndex < 0 || activeContentIndex >= playableContents.length - 1}
                      title="Bài sau"
                      aria-label="Bài sau"
                      className={`${navChevronClass} right-2 lg:right-3`}
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </>
                )}
                {activeContent.contentType === 'VIDEO' && activeContent.objectId && (
                  <VideoPlayer 
                    contentId={activeContent.id} 
                    videoId={activeContent.objectId} 
                    onComplete={(contentId) => {
                      handleComplete(contentId);
                      // Chỉ tự động nhảy sang bài tiếp nếu bài đó KHÔNG phải Quiz (TEST).
                      // Nếu bài tiếp theo là Quiz, dừng lại để học viên tự chọn.
                      const nextItem = playableContents[activeContentIndex + 1];
                      if (nextItem && nextItem.contentType !== 'TEST') {
                        goToContent(1);
                      }
                    }}
                  />
                )}
                
                {activeContent.contentType === 'POST' && (
                  <PostViewer 
                    contentId={activeContent.id}
                    contentHtml={activeContent.detail || ''}
                    onComplete={handleComplete}
                    isCompleted={completedIds.includes(activeContent.id)}
                    title={activeContent.name}
                  />
                )}
                
                {activeContent.contentType === 'TEST' && activeContent.objectId ? (
                  <TestViewer 
                    key={`${activeContent.id}`}
                    contentId={activeContent.id}
                    paperId={activeContent.objectId}
                    courseId={courseId}
                    onComplete={handleComplete}
                    isCompleted={completedIds.includes(activeContent.id)}
                    initialHistoryOpen={openQuizHistory}
                  />
                ) : null}

                {activeContent.contentType === 'TEST' && !activeContent.objectId && (
                  <div className="bg-m2-error/10 text-red-600 p-6 rounded-lg text-center font-medium border border-m2-error/30 w-full">
                    Lỗi: Bài kiểm tra chưa được gắn đề thi (paper).
                  </div>
                )}

                {activeContent.contentType === 'VIDEO' && !activeContent.objectId && (
                    <div className="bg-m2-error/10 text-red-600 p-6 rounded-lg text-center font-medium border border-m2-error/30">
                    Lỗi: Video chưa được gắn file.
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Lesson Title & Tabs below Player */}
          <div className="bg-m2-surface-container">
            <div className="px-4 py-4 lg:px-10 lg:py-8">
              <h2 className="text-lg lg:text-2xl font-bold text-m2-on-surface mb-4 lg:mb-6">
                {activeSkillFile
                  ? activeSkillFile.name
                  : activeMaterial
                    ? activeMaterial.title
                    : activeLiveSession
                      ? (activeLiveSession.sessionTitle || activeLiveSession.title)
                      : (activeContent?.name || 'Vui lòng chọn bài học')}
              </h2>
              
              <nav className="flex space-x-6 border-b border-m2-outline-variant">
                  <button 
                    onClick={() => setActiveTab('overview')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === 'overview' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline'}`}>
                      Tổng quan
                  </button>
                  {activeLiveSession && (
                    <button
                      onClick={() => setActiveTab('session-materials')}
                      className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === 'session-materials' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline'}`}
                    >
                      Tài liệu{sessionMaterialsCount > 0 ? ` (${sessionMaterialsCount})` : ''}
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('whiteboards')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === 'whiteboards' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline'}`}>
                    Bảng minh họa
                  </button>
                  <button 
                    onClick={() => setActiveTab('comments')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === 'comments' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline'}`}>
                      Bình luận
                  </button>
                  <button
                    onClick={() => setActiveTab('homeworks')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === 'homeworks' ? 'border-m2-primary text-m2-primary' : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline'}`}>
                    BTVN{homeworks.length > 0 ? ` (${homeworks.length})` : ''}
                  </button>
              </nav>

              <div className="py-6">
                {activeTab === 'overview' && (
                    <div className="text-m2-on-surface">
                      {activeLiveSession ? (
                        <>
                          <p className="text-m2-on-surface font-semibold mb-2">{activeLiveSession.title}</p>
                          <p className="text-sm text-m2-on-surface-variant mb-3">
                              {formatDbDateTimeVi(activeLiveSession.startTime)} → {formatDbDateTimeVi(activeLiveSession.endTime)}
                          </p>
                            <div className="flex flex-wrap gap-2 mb-4 text-xs font-medium">
                              <span className={`px-2.5 py-1 rounded-full ${
                                activeLiveSession.displayStatus === 'IN_PROGRESS' ? 'bg-m2-error/10 text-red-700'
                                : activeLiveSession.displayStatus === 'COMPLETED' ? 'bg-m2-surface-high text-m2-on-surface-variant'
                                : activeLiveSession.displayStatus === 'UPCOMING_SOON' ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-m2-primary/10 text-m2-primary'
                              }`}>
                                {activeLiveSession.displayStatus === 'IN_PROGRESS' ? 'Đang diễn ra'
                                  : activeLiveSession.displayStatus === 'COMPLETED' ? 'Đã diễn ra'
                                  : activeLiveSession.displayStatus === 'UPCOMING_SOON' ? 'Sắp diễn ra'
                                  : 'Chưa diễn ra'}
                              </span>
                            </div>
                          {activeLiveSession.sessionContent && (
                            <div className="mb-4">
                              <h4 className="text-sm font-bold text-m2-on-surface mb-2">Nội dung buổi học</h4>
                              <div className="prose prose-blue max-w-none text-sm whitespace-pre-line" dangerouslySetInnerHTML={{ __html: activeLiveSession.sessionContent }} />
                            </div>
                          )}
                          {activeLiveSession.sessionOutput && (
                            <div className="mb-4">
                              <h4 className="text-sm font-bold text-m2-on-surface mb-2">Output</h4>
                              <div className="text-sm text-m2-on-surface whitespace-pre-line">{activeLiveSession.sessionOutput}</div>
                            </div>
                          )}
                          {activeLiveSession.sessionHomework && (
                            <div className="mb-4">
                              <h4 className="text-sm font-bold text-m2-on-surface mb-2">BTVN</h4>
                              <div className="text-sm text-orange-700 whitespace-pre-line">{activeLiveSession.sessionHomework}</div>
                            </div>
                          )}
                          {sessionMaterialsCount > 0 && (
                            <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2.5 text-sm text-m2-on-surface-variant">
                              Buổi này có{' '}
                              <button
                                type="button"
                                onClick={() => setActiveTab('session-materials')}
                                className="font-semibold text-m2-primary hover:underline"
                              >
                                {sessionMaterialsCount} tài liệu
                              </button>
                              {' '}— mở tab Tài liệu để xem.
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <p>Đây là phần mô tả tổng quan của bài học <strong>{activeContent?.name}</strong>.</p>
                          {activeContent?.detail && activeContent.contentType !== 'POST' && (
                         <div className="mt-4 prose prose-blue max-w-none" dangerouslySetInnerHTML={{__html: activeContent.detail}} />
                          )}
                        </>
                      )}
                    </div>
                 )}
                 {activeTab === 'whiteboards' && (
                    <CourseWhiteboardGallery
                      courseId={courseId}
                      refreshKey={whiteboardRefresh}
                      onOpenMaterial={(materialId) => {
                        void openMaterialFromGallery(materialId);
                      }}
                    />
                 )}
                 {activeTab === 'session-materials' && activeLiveSession && (
                    <div className="space-y-4">
                      {!activeLiveSession.sessionId && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          Buổi live này chưa được gắn với buổi học trong chương trình — tài liệu theo buổi có thể chưa hiển thị.
                        </p>
                      )}
                      {sessionMaterialsCount === 0 ? (
                        <p className="text-sm text-m2-on-surface-variant">Chưa có tài liệu gắn với buổi học này.</p>
                      ) : (
                        <>
                          <div className="space-y-2">
                            {sessionMaterials.map((mat) => (
                              <button
                                key={mat.id}
                                type="button"
                                onClick={() => setSessionMaterialPreview(mat)}
                                className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                                  sessionMaterialPreview?.id === mat.id
                                    ? 'border-m2-primary bg-m2-primary/10 text-m2-primary'
                                    : 'border-m2-outline-variant text-m2-on-surface hover:bg-m2-primary/5'
                                }`}
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  {isSampleProjectMaterial(mat) ? (
                                    <FolderIcon className="h-4 w-4 shrink-0 text-amber-600" />
                                  ) : (
                                    <DocumentIcon className="h-4 w-4 shrink-0" />
                                  )}
                                  <span className="truncate font-medium">{mat.title}</span>
                                </span>
                                <span className="text-[10px] uppercase text-m2-on-surface-variant shrink-0">
                                  {isSampleProjectMaterial(mat) ? 'FOLDER' : mat.fileType || 'FILE'}
                                </span>
                              </button>
                            ))}
                          </div>
                          {sessionMaterialPreview && (
                            <div className="mt-2 rounded-lg border border-m2-outline-variant min-h-[360px] relative flex flex-col">
                              <button
                                type="button"
                                onClick={() => goToSessionMaterial(-1)}
                                disabled={activeSessionMaterialIndex <= 0}
                                title="Tài liệu trước"
                                aria-label="Tài liệu trước"
                                className={`${navChevronClass} left-2 lg:left-3`}
                              >
                                <ChevronLeftIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => goToSessionMaterial(1)}
                                disabled={
                                  activeSessionMaterialIndex < 0 ||
                                  activeSessionMaterialIndex >= sessionMaterials.length - 1
                                }
                                title="Tài liệu sau"
                                aria-label="Tài liệu sau"
                                className={`${navChevronClass} right-2 lg:right-3`}
                              >
                                <ChevronRightIcon className="h-5 w-5" />
                              </button>
                              {isSampleProjectMaterial(sessionMaterialPreview) ? (
                                <div className="p-3 min-h-[360px]">
                                  <SampleProjectViewer
                                    courseId={courseId}
                                    materialId={sessionMaterialPreview.id}
                                    title={sessionMaterialPreview.title}
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className="flex-1 min-h-[220px] overflow-hidden relative">
                                    <DocumentViewer
                                      fileUrl={sessionMaterialPreview.fileUrl}
                                      fileType={sessionMaterialPreview.fileType || ''}
                                      title={sessionMaterialPreview.title}
                                      materialId={sessionMaterialPreview.id}
                                      courseId={courseId}
                                      canAnnotate={canTeach}
                                      compact
                                      onWhiteboardSaved={() => setWhiteboardRefresh((k) => k + 1)}
                                      editRequest={
                                        whiteboardEditRequest?.materialId === sessionMaterialPreview.id
                                          ? whiteboardEditRequest
                                          : null
                                      }
                                      onEditRequestHandled={() => setWhiteboardEditRequest(null)}
                                    />
                                  </div>
                                  <div className="shrink-0 border-t border-m2-outline-variant px-3 pb-2">
                                    <MaterialWhiteboardList
                                      courseId={courseId}
                                      materialId={sessionMaterialPreview.id}
                                      refreshKey={whiteboardRefresh}
                                      onEditWhiteboard={canTeach ? setWhiteboardEditRequest : undefined}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                 )}
                 {activeTab === 'comments' && (
                    <div className="mt-4">
                      {activeContentId ? (
                        <CommentSection courseContentId={activeContentId} />
                      ) : (
                        <p className="text-sm text-m2-on-surface-variant">
                          Bình luận gắn với nội dung video/bài học. Vui lòng chọn một bài học video trong mục Nội dung để bình luận.
                        </p>
                      )}
                    </div>
                 )}
                 {activeTab === 'homeworks' && (
                    <div className="mt-2 space-y-3">
                      {homeworksLoading ? (
                        <p className="text-sm text-m2-on-surface-variant">Đang tải BTVN...</p>
                      ) : homeworks.length === 0 ? (
                        <p className="text-sm text-m2-on-surface-variant">Không có BTVN cho lớp của bạn.</p>
                      ) : (
                        homeworks.map((hw: any) => {
                          const isSelf = hw.homeworkType === 'self_practice'
                          const statusText = isSelf
                            ? hw.submissionStatus === 'completed'
                              ? 'Hoàn thành'
                              : 'Chưa làm'
                            : hw.submissionStatus || 'Chưa làm'
                          return (
                          <button
                            key={hw.id}
                            type="button"
                            onClick={() => navigate(`/student/courses/${courseId}/homeworks/${hw.id}`)}
                            className="w-full text-left rounded-lg border border-m2-outline-variant px-4 py-3 hover:bg-m2-primary/5"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-semibold text-m2-on-surface">
                                {hw.name}
                                {isSelf ? (
                                  <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                                    Tự thực hành
                                  </span>
                                ) : null}
                              </div>
                              <span className="text-[11px] shrink-0 px-2 py-0.5 rounded-full bg-m2-surface-high text-m2-on-surface-variant">
                                {statusText}
                              </span>
                            </div>
                            <div className="text-xs text-m2-on-surface-variant mt-1">
                              {hw.visibleCommentCount
                                ? `${hw.visibleCommentCount} comment`
                                : 'Chưa có comment'}
                              {hw.deadline
                                ? ` · Hạn: ${new Date(hw.deadline).toLocaleDateString('vi-VN')}`
                                : ''}
                            </div>
                            {hw.adminFinalNote && (
                              <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-950">
                                <span className="font-semibold">Nhận xét admin: </span>
                                {hw.adminFinalNote}
                              </div>
                            )}
                          </button>
                          )
                        })
                      )}
                    </div>
                 )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - desktop only */}
        {sidebarExpanded && (
          <aside className="hidden lg:flex w-[380px] bg-white border-l border-m2-outline flex-col flex-shrink-0 sticky top-14 h-[calc(100vh-56px)] z-20 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)]">
            <div className="w-[380px] h-full">
              {renderSidebarInner({ showCollapse: true })}
            </div>
          </aside>
        )}

        {/* Floating restore when desktop sidebar is hidden */}
        {!sidebarExpanded && (
          <div className="hidden lg:block fixed right-4 top-1/2 -translate-y-1/2 z-20">
            <button
              type="button"
              onClick={() => setSidebarExpanded(true)}
              className="flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl transition-all border border-m2-outline-variant text-m2-on-surface-variant hover:text-m2-primary"
              title="Hiện menu nội dung"
              aria-label="Hiện menu nội dung"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Mobile drawer */}
        {mobileSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-black/50"
              aria-label="Đóng nội dung"
              onClick={closeMobileSidebar}
            />
            <aside className="absolute inset-y-0 right-0 w-[min(100vw,380px)] bg-white shadow-2xl flex flex-col">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-m2-outline bg-white shrink-0">
                <span className="font-bold text-sm text-m2-on-surface">Nội dung khóa học</span>
                <button
                  type="button"
                  onClick={closeMobileSidebar}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-m2-on-surface-variant hover:bg-m2-surface-high"
                  aria-label="Đóng"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden">
                {renderSidebarInner()}
              </div>
            </aside>
          </div>
        )}
      </main>

      <CourseReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        courseId={courseId}
        courseName={courseData?.name}
      />
    </div>
  );
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// ClassroomSessionCard â€“ hiá»ƒn thá»‹ 1 buá»•i há»c trong tab Lá»›p há»c
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ClassroomSessionCard: React.FC<{ session: any; isActive?: boolean; compact?: boolean; onSelect: () => void }> = ({ session, isActive, compact, onSelect }) => {
  const statusConfig: Record<string, { label: string; cls: string }> = {
    UPCOMING_SOON: { label: 'Sắp diễn ra', cls: 'bg-yellow-100 text-yellow-800' },
    IN_PROGRESS: { label: 'Đang diễn ra', cls: 'bg-red-100 text-red-700' },
    COMPLETED: { label: 'Đã diễn ra', cls: 'bg-m2-surface-high text-m2-on-surface-variant' },
    CANCELLED: { label: 'Đã huỷ', cls: 'bg-orange-100 text-orange-700' },
    SCHEDULED: { label: 'Chưa diễn ra', cls: 'bg-m2-primary/10 text-m2-primary' },
  };
  const sc = statusConfig[session.displayStatus] || statusConfig.SCHEDULED;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isActive ? 'true' : undefined}
      className={`w-full text-left p-3 transition-colors border-l-4 ${
        isActive
          ? 'border-m2-primary bg-m2-primary/10'
          : 'border-transparent bg-m2-surface-container hover:bg-m2-surface-low'
      }`}
    >
      <div className="flex items-start gap-2">
        <VideoCameraIcon className={`w-4 h-4 shrink-0 mt-0.5 ${isActive ? 'text-m2-primary' : 'text-m2-outline'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-sm truncate ${isActive ? 'font-bold text-m2-primary' : 'font-semibold text-m2-on-surface'}`}>
              {session.title}
            </span>
            {isActive && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 bg-m2-primary text-m2-on-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse"></span>
                Đang xem
              </span>
            )}
            {!compact && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${sc.cls}`}>{sc.label}</span>}
          </div>
          {!compact && <p className="text-[11px] text-m2-outline mt-0.5">{formatDbDateTimeVi(session.startTime)} → {formatDbDateTimeVi(session.endTime)}</p>}
        </div>
      </div>
    </button>
  );
};

function sessionRecordingUnavailableCopy(session: LiveSessionItem): {
  title: string;
  detail: string;
} {
  const status = session.displayStatus || session.status;
  if (status === 'CANCELLED') {
    return {
      title: 'Buổi học đã bị hủy',
      detail: 'Không có video ghi hình cho buổi này.',
    };
  }
  if (status === 'IN_PROGRESS') {
    return {
      title: 'Buổi học đang diễn ra',
      detail: 'Video ghi hình sẽ có sau khi buổi học kết thúc.',
    };
  }
  if (status === 'COMPLETED') {
    return {
      title: 'Video ghi hình chưa sẵn sàng',
      detail: 'Hệ thống đang chuẩn bị bản ghi. Vui lòng quay lại sau ít phút.',
    };
  }
  // SCHEDULED / UPCOMING_SOON / default
  return {
    title: 'Buổi học chưa diễn ra',
    detail: 'Video ghi hình sẽ được cập nhật sau khi buổi học kết thúc. Tài khoản của bạn vẫn hoạt động bình thường.',
  };
}

const LiveSessionVideoPlayer: React.FC<{
  session: LiveSessionItem;
  videoSpeed: number;
  onSpeedChange: (speed: number) => void;
  onEnded?: () => void;
}> = ({ session, videoSpeed, onSpeedChange, onEnded }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Only request secure stream when a recording is known, or session finished (may still be syncing).
  const hasKnownRecording = Boolean(session.hasVideo || session.recordingUrl);
  const status = session.displayStatus || session.status;
  const mayHaveRecording = hasKnownRecording || status === 'COMPLETED';
  const {
    streamUrl: videoUrl,
    format,
    error: leaseError,
    videoRef,
    applyResumeOnMetadata,
    recoverFromStreamError,
  } = useSecurePlaybackStream('recording', session.id, {
    active: mayHaveRecording,
  });

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = videoSpeed;
    }
  }, [videoSpeed, videoRef]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const anyDoc = document as Document & { webkitFullscreenElement?: Element | null };
      setIsFullscreen(Boolean(document.fullscreenElement || anyDoc.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      const anyDoc = document as Document & {
        fullscreenElement?: Element | null;
        webkitFullscreenElement?: Element | null;
        exitFullscreen?: () => Promise<void>;
        webkitExitFullscreen?: () => void;
      };
      if (anyDoc.fullscreenElement || anyDoc.webkitFullscreenElement) {
        if (anyDoc.exitFullscreen) await anyDoc.exitFullscreen();
        else anyDoc.webkitExitFullscreen?.();
        return;
      }

      const container = containerRef.current;
      if (container) {
        const anyEl = container as HTMLElement & {
          requestFullscreen?: () => Promise<void>;
          webkitRequestFullscreen?: () => void;
        };
        if (anyEl.requestFullscreen) await anyEl.requestFullscreen();
        else anyEl.webkitRequestFullscreen?.();
        return;
      }

      const iosVideo = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
      iosVideo?.webkitEnterFullscreen?.();
    } catch (error) {
      console.error('Fullscreen failed', error);
      const iosVideo = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
      iosVideo?.webkitEnterFullscreen?.();
    }
  };

  const unavailable = sessionRecordingUnavailableCopy(session);
  const isSoftUnavailable =
    !mayHaveRecording ||
    (Boolean(leaseError) &&
      !leaseError?.includes('khóa') &&
      !leaseError?.includes('thiết bị'));

  return (
    <div
      ref={containerRef}
      className={`relative z-10 overflow-hidden border border-m2-outline-variant bg-[#101010] shadow-xl ${
        isFullscreen ? 'h-screen w-screen rounded-none' : 'h-full rounded-none lg:rounded-2xl'
      }`}
    >
      {videoUrl ? (
        <div className="relative h-full">
          <SecureVideo
            format={format}
            src={videoUrl}
            videoRef={videoRef}
            controls
            autoPlay
            preload="metadata"
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            onContextMenu={(event) => event.preventDefault()}
            onDoubleClick={(event) => {
              event.preventDefault();
              void toggleFullscreen();
            }}
            onError={() => recoverFromStreamError()}
            onEnded={() => onEnded?.()}
            className="h-full w-full object-contain bg-black"
            onLoadedMetadata={(event) => {
              applyResumeOnMetadata(event.currentTarget, videoSpeed);
            }}
          />
          <VideoWatermark />
          <div className="absolute left-4 top-4 z-30 flex items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-xs text-white backdrop-blur">
            <span className="truncate max-w-[42vw] font-medium">{session.title}</span>
          </div>
          <div className="absolute right-4 top-4 z-30 flex items-center gap-2 text-white">
            <div className="flex items-center gap-2 rounded-full bg-black/70 px-3 py-2 text-xs backdrop-blur">
              <span>Tốc độ</span>
              <select
                value={videoSpeed}
                onChange={(event) => onSpeedChange(Number(event.target.value))}
                className="rounded border border-white/20 bg-black/80 px-1.5 py-0.5 text-white outline-none"
              >
                {[1, 1.25, 1.5, 1.75, 2].map((speed) => (
                  <option key={speed} value={speed} style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                    {speed}x
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => void toggleFullscreen()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur hover:bg-black/90"
              aria-label={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
              title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
            >
              <i className={`fas fa-${isFullscreen ? 'compress' : 'expand'}`}></i>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 bg-black px-6 text-center">
          {!mayHaveRecording ? (
            <>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white/80">
                <VideoCameraIcon className="h-7 w-7" />
              </div>
              <p className="text-base font-medium text-white/90">{unavailable.title}</p>
              <p className="max-w-md text-sm text-white/55">{unavailable.detail}</p>
            </>
          ) : leaseError ? (
            isSoftUnavailable ? (
              <>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white/80">
                  <VideoCameraIcon className="h-7 w-7" />
                </div>
                <p className="text-base font-medium text-white/90">{unavailable.title}</p>
                <p className="max-w-md text-sm text-white/55">{leaseError}</p>
              </>
            ) : (
              <span className="text-sm text-red-300">{leaseError}</span>
            )
          ) : (
            <span className="text-sm text-white/70">Đang chuẩn bị video...</span>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentLearningPage;

